// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web — components/CentroCustoSearchCombobox.tsx
//
//  Escolhe um centro de custo SEM pedir ID: busca por referência (código) ou
//  descrição, rótulo "REF — Descrição". A lista inteira já vem em cache
//  (GET /api/centros-custo, mesma chave de useCentrosCusto), então a busca é local.
//
//  Só lista ativos; o valor salvo aparece mesmo se o centro foi inativado depois.
// ════════════════════════════════════════════════════════════════════════════

import { useQuery } from "@tanstack/react-query";
import { EntityCombobox } from "@/components/EntityCombobox";
import { apiClient } from "@/lib/apiClient";
import { compararTexto } from "@/lib/consultas";

export interface CentroCustoItem {
  id: number;
  descricao?: string;
  referencia?: string | null;
  contaContabilId?: number | null;
  isAtivo?: boolean;
}

interface CentroCustoSearchComboboxProps {
  value?: number | null;
  onChange?: (id: number | null) => void;
  /** Objeto inteiro (ex.: derivar a conta contábil do centro). */
  onSelect?: (centro: CentroCustoItem | null) => void;
  /** Lista também centros inativos (padrão: não). */
  incluirInativos?: boolean;
  limite?: number;
  placeholder?: string;
  permitirLimpar?: boolean;
  disabled?: boolean;
  className?: string;
  id?: string;
  "aria-label"?: string;
}

const normalizar = (s: string) => s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

/** Hook da lista em cache (mesma queryKey dos hooks de cadastro — não duplica requisição). */
export function useCentrosCustoLookup() {
  return useQuery({
    queryKey: ["centros-custo"],
    queryFn: async () => {
      const { data } = await apiClient.get<CentroCustoItem[]>("/api/centros-custo");
      return data;
    },
    staleTime: 60_000,
  });
}

export function CentroCustoSearchCombobox({
  incluirInativos, limite = 80, placeholder = "Selecione o centro de custo...", value, ...props
}: CentroCustoSearchComboboxProps) {
  const { data: centros = [], dataUpdatedAt } = useCentrosCustoLookup();

  const buscar = async (termo: string) => {
    const t = normalizar(termo);
    return centros
      .filter((c) =>
        (incluirInativos || c.isAtivo !== false)
        && (!t
          || normalizar(c.descricao ?? "").includes(t)
          || normalizar(c.referencia ?? "").startsWith(t)))
      .sort((a, b) => compararTexto(a.descricao ?? "", b.descricao ?? ""))
      .slice(0, limite);
  };

  const atual = value ? centros.find((c) => c.id === value) ?? null : null;

  return (
    <EntityCombobox<CentroCustoItem>
      {...props}
      value={value ?? null}
      itemInicial={atual}
      // A chave muda quando a lista carrega/atualiza: a busca local roda de novo.
      queryKey={`centro-custo-${incluirInativos ? "i" : ""}-${dataUpdatedAt}`}
      buscar={buscar}
      getId={(c) => c.id}
      getCodigo={(c) => c.referencia}
      getDescricao={(c) => c.descricao ?? ""}
      getExtra={(c) => (c.isAtivo === false ? "Inativo" : null)}
      debounceMs={0}
      placeholder={placeholder}
      placeholderBusca="Buscar por referência ou descrição..."
    />
  );
}
